import type { ReactNode } from 'react'

import {
  EmptyState,
  IconBills,
  IconCreditCard,
  PrimaryButton,
  Space,
  Tag,
  Text,
  TextLoader,
} from '@procurify/ui'

import { FormattedShortDate } from '@webapp/core/components/FormattedDate'
import { PaginationControls } from '@webapp/core/components/PaginationControls'
import { useProcurifyIntl } from '@webapp/hooks'
import { type CreditCardItem } from '@webapp/modules/accounts-payable/entities/CreditCardItem'
import { type ReconcileMatch } from '@webapp/modules/accounts-payable/entities/ReconcileMatch'
import messages from './messages'
import { MatchGroupStyled } from './styles/MatchGroupStyled'

export interface IMatchedTransactionListProps {
  /** CONFIRMED matches only — the presenter filters (BHV-039) */
  matches: ReconcileMatch[]
  /** metadata.summary.total_amount */
  clearedBalance: Nullable<number>
  currencyName: string
  isLoading: boolean
  /** Empty-state link → Unmatched tab (FS routing, BHV-039) */
  onBeginMatching: () => void
  /** Slot for the Edit item / Unmatch item menu (ADR-9) */
  renderRowMenu: (item: CreditCardItem, match: ReconcileMatch) => ReactNode
}

export const MatchedTransactionList = ({
  matches,
  clearedBalance,
  currencyName,
  isLoading,
  onBeginMatching,
  renderRowMenu,
}: IMatchedTransactionListProps) => {
  const intl = useProcurifyIntl()

  if (isLoading) {
    return <TextLoader />
  }

  if (matches.length === 0) {
    return (
      <EmptyState
        header={intl.formatMessage(messages.emptyStateHeader)}
        button={
          <PrimaryButton type='button' onClick={onBeginMatching}>
            {intl.formatMessage(messages.beginMatchingButton)}
          </PrimaryButton>
        }
      />
    )
  }

  return (
    <PaginationControls id='ap-reconcile-matched'>
      <Space>
        {matches.map((match) => (
          <MatchGroupStyled key={match.id}>
            <Space size='small'>
              {match.creditcardItems.map((item) => (
                <Space
                  key={item.id}
                  direction='horizontal'
                  justify='space-between'
                  align='center'
                >
                  <Space direction='horizontal' align='center'>
                    {item.packingSlips.length > 0 ? (
                      <IconBills
                        size={24}
                        color='darkGrey'
                        ariaLabel={intl.formatMessage(
                          messages.receiptAttachedAriaLabel
                        )}
                      />
                    ) : (
                      <IconCreditCard
                        size={24}
                        color='darkGrey'
                        ariaLabel={intl.formatMessage(
                          messages.noReceiptAriaLabel
                        )}
                      />
                    )}
                    {item.po && (
                      <Space direction='horizontal' size='small' align='center'>
                        <Tag>{intl.formatMessage(messages.poTag)}</Tag>
                        <Text bold autoMargin={false}>
                          {item.po.number}
                        </Text>
                      </Space>
                    )}
                    {item.expense && (
                      <Space direction='horizontal' size='small' align='center'>
                        <Tag>{intl.formatMessage(messages.rfeTag)}</Tag>
                        <Text bold autoMargin={false}>
                          {item.expense.expenseReportId}
                        </Text>
                      </Space>
                    )}
                    <Text autoMargin={false}>{item.descriptionLabel}</Text>
                  </Space>
                  <Space direction='horizontal' align='center'>
                    <Text autoMargin={false}>
                      <FormattedShortDate date={item.receivedOn} />
                    </Text>
                    <Text color='textSecondary' autoMargin={false}>
                      {item.accountCodeLabel}
                    </Text>
                    <Text bold autoMargin={false}>
                      {item.totalCost.toFixed(2)} {item.currency.name}
                    </Text>
                    {renderRowMenu(item, match)}
                  </Space>
                </Space>
              ))}
            </Space>
          </MatchGroupStyled>
        ))}

        <Space direction='horizontal' justify='space-between' align='center'>
          <Space direction='horizontal' justify='end' align='center'>
            <PaginationControls.Page />
          </Space>
          {clearedBalance !== null && (
            <Text bold autoMargin={false}>
              {intl.formatMessage(messages.clearedBalanceLabel, {
                amount: clearedBalance.toFixed(2),
                currency: currencyName,
              })}
            </Text>
          )}
        </Space>
      </Space>
    </PaginationControls>
  )
}

MatchedTransactionList.displayName = 'MatchedTransactionList'
